import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Save, Loader2, Plus, Trash2, ChevronUp, ChevronDown, ChevronRight } from 'lucide-react';
import api from '../api';

function FieldRow({ label, value, onChange, type = 'text', mono = false }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">{label}</label>
      {type === 'textarea' ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={2}
          className={`w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9962E]/40 resize-none ${mono ? 'font-mono' : ''}`} />
      ) : (
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
          className={`w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9962E]/40 ${mono ? 'font-mono' : ''}`} />
      )}
    </div>
  );
}

function ListField({ label, items, onChange }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">{label}</label>
      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={i} className="flex gap-2">
            <input value={item} onChange={(e) => { const arr = [...items]; arr[i] = e.target.value; onChange(arr); }}
              className="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9962E]/40" />
            <button onClick={() => onChange(items.filter((_, j) => j !== i))} className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
        <button onClick={() => onChange([...items, ''])} className="flex items-center gap-1.5 text-xs text-[#C9962E] hover:underline">
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </div>
    </div>
  );
}

export default function SiteSettings() {
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  const { data: settings, isLoading } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: () => api.get('/admin/settings').then((r) => r.data.data),
  });

  const initialized = React.useRef(false);
  useEffect(() => {
    if (settings && !initialized.current) {
      initialized.current = true;
      setForm({
        ...settings,
        contact_cta: settings.contact_cta ?? { label: 'Contact', to: '/contact' },
      });
    }
  }, [settings]);

  const set = (path, val) => {
    setForm((prev) => {
      const next = { ...prev };
      const keys = path.split('.');
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) {
        cur[keys[i]] = { ...cur[keys[i]] };
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = val;
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        logo_url: form.logo_url,
        contact: form.contact,
        socials: form.socials,
        nav_links: form.nav_links,
        footer_columns: form.footer_columns,
        copyright: form.copyright,
        founded_year: form.founded_year,
        led_by: form.led_by,
        contact_cta: form.contact_cta,
      };
      await api.put('/admin/settings', payload);
      qc.invalidateQueries({ queryKey: ['admin-settings'] });
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (isLoading || !form) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-8 max-w-2xl" data-testid="site-settings">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1F2D45]">Site Settings</h1>
          <p className="text-slate-500 text-sm mt-1">Manage global site contact info and branding.</p>
        </div>
        <button onClick={handleSave} disabled={saving} data-testid="save-settings-btn"
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#1F2D45] text-white text-sm font-semibold hover:bg-[#2a3d5e] disabled:opacity-60 transition-colors">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          <Save className="w-4 h-4" /> Save Changes
        </button>
      </div>

      <Section title="Branding">
        <FieldRow label="Logo URL" value={form.logo_url || ''} onChange={(v) => set('logo_url', v)} mono />
        <FieldRow label="Copyright Text" value={form.copyright || ''} onChange={(v) => set('copyright', v)} />
        <div className="grid sm:grid-cols-2 gap-4">
          <FieldRow label="Founded Year (footer)" value={form.founded_year || ''} onChange={(v) => set('founded_year', v)} />
          <FieldRow label="Led By (footer)" value={form.led_by || ''} onChange={(v) => set('led_by', v)} />
        </div>
        <p className="text-[11px] text-slate-400">These appear in the footer as: Founded {'{year}'} · Led by {'{name}'}</p>
      </Section>

      <Section title="Contact">
        <ListField label="Address (legacy — used only when no Offices defined below)" items={form.contact?.address || []} onChange={(v) => set('contact.address', v)} />
        <ListField label="Phone (shared)" items={form.contact?.phone || []} onChange={(v) => set('contact.phone', v)} />
        <ListField label="Email (shared)" items={form.contact?.email || []} onChange={(v) => set('contact.email', v)} />
        <FieldRow label="Website" value={form.contact?.website || ''} onChange={(v) => set('contact.website', v)} mono />
      </Section>

      <Section title="Offices (Branch Locations)">
        <p className="text-[12px] text-slate-500 -mt-1">
          Each office gets its own card + map on the Contact page. Add as many as you need.
        </p>
        <OfficesEditor value={form.contact?.offices || []} onChange={(v) => set('contact.offices', v)} />
      </Section>

      <Section title="Header — Contact Button">
        <p className="text-[12px] text-slate-500 -mt-1">
          Controls the gold "Contact" CTA button in the top navigation bar.
        </p>
        <FieldRow label="Button Label" value={form.contact_cta?.label || ''} onChange={(v) => set('contact_cta.label', v)} />
        <FieldRow label="Button Link (e.g. /contact)" value={form.contact_cta?.to || ''} onChange={(v) => set('contact_cta.to', v)} mono />
      </Section>

      <Section title="Social Links">
        <p className="text-[12px] text-slate-500 -mt-1">
          Social platform icons shown in the footer. Add any platform — supported icons: facebook, twitter/x, linkedin, youtube, instagram, discord, whatsapp, github, threads.
        </p>
        <SocialLinksEditor value={form.socials || []} onChange={(v) => set('socials', v)} />
      </Section>

      <Section title="Navigation Menu (Top Nav)">
        <p className="text-[12px] text-slate-500 -mt-1">
          Build your top navigation bar. Click <strong>+</strong> on any item to add dropdown submenu items.
        </p>
        <NavLinksEditor value={form.nav_links || []} onChange={(v) => set('nav_links', v)} />
      </Section>
    </div>
  );
}

function NavLinksEditor({ value, onChange }) {
  const items = Array.isArray(value) ? value : [];

  const updateItem = (idx, key, v) => {
    const next = items.map((item, i) => (i === idx ? { ...item, [key]: v } : item));
    onChange(next);
  };

  const updateChild = (parentIdx, childIdx, key, v) => {
    const next = items.map((item, i) => {
      if (i !== parentIdx) return item;
      const children = Array.isArray(item.children) ? item.children : [];
      return {
        ...item,
        children: children.map((c, ci) => (ci === childIdx ? { ...c, [key]: v } : c)),
      };
    });
    onChange(next);
  };

  const removeItem = (idx) => {
    if (!window.confirm('Remove this menu item?')) return;
    onChange(items.filter((_, i) => i !== idx));
  };

  const removeChild = (parentIdx, childIdx) => {
    const next = items.map((item, i) => {
      if (i !== parentIdx) return item;
      const children = (item.children || []).filter((_, ci) => ci !== childIdx);
      return { ...item, children: children.length > 0 ? children : undefined };
    });
    onChange(next);
  };

  const addItem = () => {
    onChange([...items, { label: '', to: '' }]);
  };

  const addChild = (parentIdx) => {
    const next = items.map((item, i) => {
      if (i !== parentIdx) return item;
      const children = Array.isArray(item.children) ? item.children : [];
      return { ...item, children: [...children, { label: '', to: '' }] };
    });
    onChange(next);
  };

  const moveItem = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= items.length) return;
    const next = [...items];
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    onChange(next);
  };

  const moveChild = (parentIdx, childIdx, dir) => {
    const newIdx = childIdx + dir;
    const children = items[parentIdx]?.children || [];
    if (newIdx < 0 || newIdx >= children.length) return;
    const next = items.map((item, i) => {
      if (i !== parentIdx) return item;
      const c = [...item.children];
      [c[childIdx], c[newIdx]] = [c[newIdx], c[childIdx]];
      return { ...item, children: c };
    });
    onChange(next);
  };

  const inputCls = 'w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9962E]/40';

  return (
    <div className="space-y-3" data-testid="nav-links-editor">
      {items.length === 0 && (
        <p className="text-[12px] text-slate-500 italic">No nav links yet. Click + Add Menu Item below.</p>
      )}

      {items.map((item, i) => {
        const hasChildren = Array.isArray(item.children) && item.children.length > 0;
        const isParent = hasChildren || item.children === undefined;
        return (
          <div
            key={i}
            className={`rounded-lg border ${hasChildren ? 'border-[#C9962E]/30 bg-[#C9962E]/[0.03]' : 'border-slate-200 bg-slate-50/60'} p-4 space-y-3`}
            data-testid={`nav-item-${i}`}
          >
            {/* Parent item row */}
            <div className="flex items-start gap-2">
              <div className="flex flex-col gap-0.5 pt-1">
                <button
                  type="button"
                  onClick={() => moveItem(i, -1)}
                  disabled={i === 0}
                  className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Move up"
                >
                  <ChevronUp className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => moveItem(i, 1)}
                  disabled={i === items.length - 1}
                  className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Move down"
                >
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </div>

              <div className="flex-1 space-y-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Label</label>
                    <input
                      type="text"
                      value={item.label || ''}
                      onChange={(e) => updateItem(i, 'label', e.target.value)}
                      placeholder="e.g. Services"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Link (URL path)</label>
                    <input
                      type="text"
                      value={item.to || ''}
                      onChange={(e) => updateItem(i, 'to', e.target.value)}
                      placeholder="e.g. /services"
                      className={`${inputCls} font-mono text-xs`}
                    />
                  </div>
                </div>

                {/* Children */}
                {hasChildren && (
                  <div className="ml-3 mt-2 space-y-2 border-l-2 border-[#C9962E]/20 pl-3">
                    <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                      <ChevronRight className="w-3 h-3" /> Submenu items
                    </p>
                    {item.children.map((child, ci) => (
                      <div key={ci} className="flex items-start gap-2">
                        <div className="flex flex-col gap-0.5 pt-1">
                          <button
                            type="button"
                            onClick={() => moveChild(i, ci, -1)}
                            disabled={ci === 0}
                            className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ChevronUp className="w-3 h-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveChild(i, ci, 1)}
                            disabled={ci === item.children.length - 1}
                            className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ChevronDown className="w-3 h-3" />
                          </button>
                        </div>
                        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <input
                            type="text"
                            value={child.label || ''}
                            onChange={(e) => updateChild(i, ci, 'label', e.target.value)}
                            placeholder="Label"
                            className={inputCls}
                          />
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={child.to || ''}
                              onChange={(e) => updateChild(i, ci, 'to', e.target.value)}
                              placeholder="/path"
                              className={`flex-1 ${inputCls} font-mono text-xs`}
                            />
                            <button
                              type="button"
                              onClick={() => removeChild(i, ci)}
                              className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors shrink-0"
                              title="Remove submenu item"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addChild(i)}
                      className="flex items-center gap-1 text-[11px] text-[#C9962E] hover:underline"
                    >
                      <Plus className="w-3 h-3" /> Add Submenu Item
                    </button>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-1 shrink-0 pt-0.5">
                {!hasChildren && (
                  <button
                    type="button"
                    onClick={() => addChild(i)}
                    className="p-1.5 rounded-md text-slate-400 hover:text-[#C9962E] hover:bg-[#C9962E]/10 transition-colors"
                    title="Add submenu item (creates dropdown)"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => removeItem(i)}
                  className="p-1.5 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                  data-testid={`nav-item-remove-${i}`}
                  title="Remove menu item"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        );
      })}

      <button
        type="button"
        onClick={addItem}
        className="flex items-center gap-1.5 text-xs text-[#C9962E] hover:underline"
        data-testid="nav-links-add-btn"
      >
        <Plus className="w-3.5 h-3.5" /> Add Menu Item
      </button>
    </div>
  );
}

function OfficesEditor({ value, onChange }) {
  const offices = Array.isArray(value) ? value : [];

  const update = (idx, key, v) => {
    const next = offices.map((o, i) => (i === idx ? { ...o, [key]: v } : o));
    onChange(next);
  };

  const remove = (idx) => {
    if (!window.confirm('Remove this office?')) return;
    onChange(offices.filter((_, i) => i !== idx));
  };

  const add = () => {
    onChange([
      ...offices,
      { label: 'New Office', address: '', contactPerson: '', designation: '', phone: '' },
    ]);
  };

  return (
    <div className="space-y-4" data-testid="offices-editor">
      {offices.length === 0 && (
        <p className="text-[12px] text-slate-500 italic">No offices yet. Click + Add Office below to create one.</p>
      )}
      {offices.map((o, i) => (
        <div
          key={i}
          className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 space-y-3"
          data-testid={`office-row-${i}`}
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
              Office #{i + 1}
            </span>
            <button
              type="button"
              onClick={() => remove(i)}
              className="p-1.5 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
              data-testid={`office-remove-${i}`}
              aria-label="Remove office"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          <FieldRow
            label="Label (e.g. Head Office — Bengaluru)"
            value={o.label || ''}
            onChange={(v) => update(i, 'label', v)}
          />
          <FieldRow
            label="Address"
            type="textarea"
            value={o.address || ''}
            onChange={(v) => update(i, 'address', v)}
          />
          <div className="grid sm:grid-cols-2 gap-3">
            <FieldRow
              label="Contact Person"
              value={o.contactPerson || ''}
              onChange={(v) => update(i, 'contactPerson', v)}
            />
            <FieldRow
              label="Designation"
              value={o.designation || ''}
              onChange={(v) => update(i, 'designation', v)}
            />
          </div>
          <FieldRow
            label="Phone"
            value={o.phone || ''}
            onChange={(v) => update(i, 'phone', v)}
          />
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        className="flex items-center gap-1.5 text-xs text-[#C9962E] hover:underline"
        data-testid="office-add-btn"
      >
        <Plus className="w-3.5 h-3.5" /> Add Office
      </button>
    </div>
  );
}

function SocialLinksEditor({ value, onChange }) {
  const items = Array.isArray(value) ? value : [];

  const update = (idx, key, v) => {
    const next = items.map((o, i) => (i === idx ? { ...o, [key]: v } : o));
    onChange(next);
  };

  const remove = (idx) => {
    if (!window.confirm('Remove this social link?')) return;
    onChange(items.filter((_, i) => i !== idx));
  };

  const add = () => {
    onChange([...items, { platform: '', url: '#' }]);
  };

  return (
    <div className="space-y-4" data-testid="socials-editor">
      {items.length === 0 && (
        <p className="text-[12px] text-slate-500 italic">No social links yet. Click + Add Social Link below.</p>
      )}
      {items.map((o, i) => (
        <div key={i} className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
              Link #{i + 1}
            </span>
            <button
              type="button"
              onClick={() => remove(i)}
              className="p-1.5 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
              aria-label="Remove social link"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <FieldRow
              label="Platform Name"
              value={o.platform || ''}
              onChange={(v) => update(i, 'platform', v)}
            />
            <FieldRow
              label="Profile URL"
              value={o.url || ''}
              onChange={(v) => update(i, 'url', v)}
              mono
            />
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        className="flex items-center gap-1.5 text-xs text-[#C9962E] hover:underline"
        data-testid="socials-add-btn"
      >
        <Plus className="w-3.5 h-3.5" /> Add Social Link
      </button>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-5">
      <h2 className="font-semibold text-slate-800 text-sm border-b border-slate-100 pb-3">{title}</h2>
      {children}
    </div>
  );
}
